import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../common/breadcrumb";
import AuthService from "../services/auth.service";
import ApiService, { API_URL } from "../services/api.service";
import Moment from "moment";
import "moment/locale/es";
import { SortableTh, sortRows, nextSort } from "../common/sortable-th";

Moment.locale("es");

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_MAP = {
  APROBADO: { label: "Activo", color: "#27ae60" },
  ACTIVADO: { label: "Activo", color: "#27ae60" },
  PENDIENTE: { label: "Pendiente", color: "#f39c12" },
  BLOQUEADO: { label: "Bloqueado", color: "#e74c3c" },
};

const PURCHASE_STATUS_MAP = {
  COMPLETADO: { label: "Completado", bg: "#d4edda", color: "#155724" },
  PENDIENTE: { label: "Pendiente", bg: "#fff3cd", color: "#856404" },
  CANCELADO: { label: "Cancelado", bg: "#f8d7da", color: "#721c24" },
  PAGADO: { label: "Pagado", bg: "#cce5ff", color: "#004085" },
  PROCESADO: { label: "Procesado", bg: "#e2d9f3", color: "#432874" },
};

const DELIVERY_STATUS_MAP = {
  "POR PREPARAR": { color: "#e74c3c", label: "Por preparar" },
  "LISTO PARA ENVIO": { color: "#f39c12", label: "Listo para envío" },
  ENTREGADO: { color: "#27ae60", label: "Entregado" },
};

const STATUS_CHARGE_MAP = {
  "POR COBRAR": { label: "Por cobrar", bg: "#fff3cd", color: "#856404" },
  CANCELADO:    { label: "Cancelado",  bg: "#f8d7da", color: "#721c24" },
  PAGADO:       { label: "Pagado",     bg: "#d4edda", color: "#155724" },
};

const CHARGE_STATE = {
  ACTIVO:                 "ACTIVO",
  PENDIENTE_VERIFICACION: "PENDIENTE_VERIFICACION",
  CANCELADO:              "CANCELADO",
  ANULADO:                "ANULADO",
};

const CHARGE_STATE_MAP = {
  [CHARGE_STATE.ACTIVO]:                 { label: "Registrado",                bg: "#d4edda", color: "#155724" },
  [CHARGE_STATE.PENDIENTE_VERIFICACION]: { label: "Pendiente de verificación", bg: "#fff3cd", color: "#856404" },
  [CHARGE_STATE.CANCELADO]:              { label: "Cancelado",                 bg: "#f8d7da", color: "#721c24" },
  [CHARGE_STATE.ANULADO]:                { label: "Anulado",                   bg: "#e2e3e5", color: "#383d41" },
};

// Un pago con deleted_at está anulado aunque su state diga otra cosa: los que
// se eliminaron antes de la anulación auditada conservan ACTIVO o PENDIENTE.
const isChargeCancelled = (c) => !!c.deleted_at || c.state === CHARGE_STATE.ANULADO;
const chargeStateKey = (c) => (isChargeCancelled(c) ? CHARGE_STATE.ANULADO : c.state);

// date_payment es solo fecha (DATEONLY): la hora sale de created_at, que es
// cuándo se registró el pago. Si la fecha de pago ingresada es otro día, se
// devuelve aparte para mostrarla debajo.
const chargeDateTime = (c) => {
  const paymentDay = c.date_payment ? Moment(c.date_payment).format('YYYY-MM-DD') : null;
  if (!c.created_at) return { main: paymentDay, paymentDay: null };
  const registered = Moment(c.created_at);
  return {
    main: registered.format('YYYY-MM-DD HH:mm'),
    paymentDay: paymentDay && paymentDay !== registered.format('YYYY-MM-DD') ? paymentDay : null,
  };
};

const CHARGE_SOURCE = {
  ADMIN:              "ADMIN",
  CLIENT:             "CLIENT",
  CLIENT_CHECKOUT:    "CLIENT-CHECKOUT",
  ADMIN_ALLOCATION:   "ADMIN-ALLOCATION",
  CUSTOMER_WHATSAPP:  "CUSTOMER-WHATSAPP",
};

const CHARGE_SOURCE_MAP = {
  [CHARGE_SOURCE.ADMIN]:             { label: "Admin",        icon: "fa-user-circle",  color: "#6c757d" },
  [CHARGE_SOURCE.CLIENT]:            { label: "App cliente",  icon: "fa-mobile",       color: "#27ae60" },
  [CHARGE_SOURCE.CLIENT_CHECKOUT]:   { label: "Checkout",     icon: "fa-shopping-cart",color: "#0d6efd" },
  [CHARGE_SOURCE.ADMIN_ALLOCATION]:  { label: "Asignación",   icon: "fa-exchange",     color: "#e67e22" },
  [CHARGE_SOURCE.CUSTOMER_WHATSAPP]: { label: "WhatsApp",     icon: "fa-whatsapp",     color: "#25d366" },
};

const CHANNEL_MAP = { TIENDA: "Tienda", WEB: "Web", APP: "App" };

const GENDER_MAP = {
  MASCULINO: "Masculino",
  FEMENINO: "Femenino",
  OTRO: "Otro",
};

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

function fmtMoney(val) {
  if (val == null || val === "") return "—";
  return (
    "S/ " +
    parseFloat(val).toLocaleString("es-PE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
}

function fmtDate(val) {
  if (!val) return "—";
  return Moment(val).format("DD MMM YYYY");
}

function initials(name, lastName) {
  return (
    ((name || "").charAt(0) + (lastName || "").charAt(0)).toUpperCase() || "?"
  );
}

// ─── small presentational components ────────────────────────────────────────

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status || "—", color: "#6c757d" };
  return (
    <span
      style={{
        background: s.color + "18",
        color: s.color,
        border: "1px solid " + s.color + "40",
        borderRadius: 20,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
}

function PurchaseBadge({ status }) {
  const s = PURCHASE_STATUS_MAP[status] || {
    label: status || "—",
    bg: "#e9ecef",
    color: "#495057",
  };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 10,
        padding: "2px 9px",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
}

function DeliveryStatus({ status }) {
  if (!status) return <span style={{ color: "#ccc" }}>—</span>;
  const s = DELIVERY_STATUS_MAP[status] || { color: "#6c757d", label: status };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12,
      }}
    >
      <i className='fa fa-circle' style={{ color: s.color, fontSize: 9 }} />
      {s.label}
    </span>
  );
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div
      style={{
        display: "flex",
        padding: "5px 0",
        borderBottom: "1px solid #f4f4f4",
      }}
    >
      <span
        style={{ color: "#aaa", fontSize: 12, minWidth: 110, flexShrink: 0 }}
      >
        {label}
      </span>
      <span style={{ fontSize: 12, color: "#333", fontWeight: 500 }}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color }) {
  return (
    <div className='col-6 col-lg-3 mb-2'>
      <div
        style={{
          background: "#f8f9fa",
          border: "1px solid #ebebeb",
          borderRadius: 8,
          padding: "14px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "#aaa",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                marginBottom: 4,
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#2d3436",
                lineHeight: 1.1,
              }}
            >
              {value}
            </div>
            {sub && (
              <div style={{ fontSize: 12, color: "#aaa", marginTop: 3 }}>
                {sub}
              </div>
            )}
          </div>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: color + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className={icon} style={{ color, fontSize: 15 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export class VerCustomerProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      profile: null,
      stats: null,
      purchases: [],
      addresses: [],
      clustersData: null,
      payments: [],
      activeTab: 0,
      purchaseModal: null,
      purchaseModalLoading: false,
      voucherImage: null,
      chargeModal: null,
      purchasesPage: 1,
      paymentsPage: 1,
      showCancelledPayments: false,
      // tab "Productos" -- a diferencia del resto (que se carga todo junto
      // en componentWillMount), esta se pide recién al hacer click en la
      // tab, no de entrada
      products: [],
      productsLoading: false,
      productsLoaded: false,
      productsPage: 1,
      productsSort: { key: "total", dir: "desc" },
    };
  }

  openChargeModal(charge) {
    this.setState({ chargeModal: charge });
  }

  openVoucher(filename) {
    this.setState({ voucherImage: filename });
  }

  openPurchaseModal(purchaseId) {
    const { id } = this.props.match.params;
    const user = AuthService.getCurrentUser();
    this.setState({ purchaseModal: 'loading', purchaseModalLoading: true });
    ApiService.getCustomerPurchaseDetail(user.token, id, purchaseId).then((res) => {
      this.setState({
        purchaseModal: res && res.status === 'success' ? res.data : null,
        purchaseModalLoading: false,
      });
    });
  }

  closePurchaseModal() {
    this.setState({ purchaseModal: null, purchaseModalLoading: false });
  }

  // ── tab: productos (carga perezosa, solo al hacer click en la tab) ────────

  handleTabClick(i) {
    this.setState({ activeTab: i });
    if (i === 6 && !this.state.productsLoaded) {
      this.loadProducts();
    }
  }

  loadProducts() {
    const { id } = this.props.match.params;
    const user = AuthService.getCurrentUser();
    this.setState({ productsLoading: true });
    // sin from/to: todo el historial de productos del cliente (mismo
    // endpoint que usa el reporte "Sku por Clientes")
    ApiService.getProductsForCustomer(user.token, id).then((res) => {
      this.setState({
        products: res && res.status === "success" ? (res.data.products || []) : [],
        productsLoading: false,
        productsLoaded: true,
      });
    });
  }

  handleProductsSort(key) {
    this.setState((prev) => ({
      productsSort: nextSort(prev.productsSort, key),
      productsPage: 1,
    }));
  }

  componentDidMount() {
    document.querySelector(".page-main-header") &&
      document.querySelector(".page-main-header").classList.add("open");
    document.querySelector(".page-sidebar") &&
      document.querySelector(".page-sidebar").classList.add("open");
  }

  componentWillUnmount() {
    document.querySelector(".page-main-header") &&
      document.querySelector(".page-main-header").classList.remove("open");
    document.querySelector(".page-sidebar") &&
      document.querySelector(".page-sidebar").classList.remove("open");
  }

  componentWillMount() {
    const { id } = this.props.match.params;
    const user = AuthService.getCurrentUser();

    Promise.all([
      ApiService.getCustomerProfile(user.token, id),
      ApiService.getCustomerPurchaseStats(user.token, id),
      ApiService.getCustomerPurchases(user.token, id, 50),
      ApiService.getCustomerAddresses(user.token, id),
      ApiService.getCustomerClustersAndCampaigns(user.token, id),
      ApiService.getCustomerPayments(user.token, id),
    ]).then(
      ([profileRes, statsRes, purchasesRes, addressesRes, clustersRes, paymentsRes]) => {
        this.setState({
          loading: false,
          profile:
            profileRes && profileRes.status === "success"
              ? profileRes.data
              : null,
          stats:
            statsRes && statsRes.status === "success" ? statsRes.data : null,
          purchases:
            purchasesRes && purchasesRes.status === "success"
              ? purchasesRes.data
              : [],
          addresses:
            addressesRes && addressesRes.status === "success"
              ? addressesRes.data
              : [],
          clustersData:
            clustersRes && clustersRes.status === "success"
              ? clustersRes.data
              : null,
          payments:
            paymentsRes && paymentsRes.status === "success"
              ? paymentsRes.data
              : [],
        });
      },
    );
  }

  // ── header ────────────────────────────────────────────────────────────────

  renderHeader() {
    const { profile, stats } = this.state;
    const { id } = this.props.match.params;
    const fullName = [profile.name, profile.last_name]
      .filter(Boolean)
      .join(" ");
    const profileData = profile.Profile || {};
    const phone = profileData.cell_phonenumber
      ? (profileData.country_code || "+51") + " " + profileData.cell_phonenumber
      : null;
    const credits = profile.Internal_accounts_summary
      ? parseFloat(profile.Internal_accounts_summary.amount_cash || 0).toFixed(
          0,
        )
      : "0";

    const statItems = [
      {
        label: "Créditos disponibles",
        value: credits,
        sub: "puntos",
        icon: "fa fa-database",
        color: "#27ae60",
      },
      {
        label: "Compras totales",
        value: stats ? stats.total_orders : "—",
        sub: "órdenes",
        icon: "fa fa-shopping-bag",
        color: "#2980b9",
      },
      {
        label: "Monto total compras",
        value: stats ? fmtMoney(stats.total_amount) : "—",
        sub: "en total",
        icon: "fa fa-dollar",
        color: "#e67e22",
      },
      {
        label: "Ticket promedio",
        value: stats ? fmtMoney(stats.avg_ticket) : "—",
        sub: "por compra",
        icon: "fa fa-bar-chart",
        color: "#9b59b6",
      },
    ];

    return (
      <div className='card mb-3'>
        <div className='card-body' style={{ padding: "20px 24px" }}>
          {/* ── fila superior: avatar | info | botones ── */}
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              marginBottom: 20,
            }}
          >
            {/* avatar + badge */}
            <div
              style={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #27ae60, #2ecc71)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  fontWeight: 700,
                }}
              >
                {initials(profile.name, profile.last_name)}
              </div>
              <StatusBadge status={profile.status} />
            </div>

            {/* nombre + contacto + metadata */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* nombre + botones */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <h4 style={{ margin: 0, fontWeight: 700, fontSize: 19 }}>
                    {fullName}
                  </h4>
                  {profile.top_client === "SI" && (
                    <span
                      style={{
                        fontSize: 12,
                        color: "#f39c12",
                        fontWeight: 600,
                      }}
                    >
                      <i className='fa fa-star' style={{ marginRight: 3 }} />
                      Top Cliente
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <Link
                    to={`${process.env.PUBLIC_URL}/edit-user/${id}`}
                    style={{
                      border: "1px solid #ccc",
                      color: "#555",
                      background: "#fff",
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontSize: 13,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <i className='fa fa-pencil' style={{ marginRight: 5 }} />
                    Editar
                  </Link>
                </div>
              </div>

              {/* contacto + metadata */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {phone && (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#555",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i
                        className='fa fa-phone'
                        style={{ marginRight: 7, color: "#bbb", width: 13 }}
                      />
                      {phone}
                    </span>
                  )}
                  {profile.email && (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#555",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 220,
                      }}
                      title={profile.email}
                    >
                      <i
                        className='fa fa-envelope-o'
                        style={{ marginRight: 7, color: "#bbb", width: 13 }}
                      />
                      {profile.email}
                    </span>
                  )}
                  {profile.nro_document && (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#555",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i
                        className='fa fa-id-card-o'
                        style={{ marginRight: 7, color: "#bbb", width: 13 }}
                      />
                      {profile.type_document} {profile.nro_document}
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6 }}>
                  {profile.created_at && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#bbb",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 2,
                        }}
                      >
                        Cliente desde
                      </div>
                      <div
                        style={{ fontSize: 13, fontWeight: 600, color: "#444" }}
                      >
                        {fmtDate(profile.created_at)}
                      </div>
                    </div>
                  )}
                  {profile.days_latest_purchase_date != null && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#bbb",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 2,
                        }}
                      >
                        Última compra
                      </div>
                      <div
                        style={{ fontSize: 13, fontWeight: 600, color: "#444" }}
                      >
                        Hace {profile.days_latest_purchase_date} días
                      </div>
                    </div>
                  )}
                  {stats && stats.first_purchase && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#bbb",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 2,
                        }}
                      >
                        Primera compra
                      </div>
                      <div
                        style={{ fontSize: 13, fontWeight: 600, color: "#444" }}
                      >
                        {fmtDate(stats.first_purchase)}
                      </div>
                    </div>
                  )}
                </div>
            </div>
          </div>

          {/* ── stat cards: CSS grid que reacciona al ancho del contenedor ── */}
          <div
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 12,
            }}
          >
            {statItems.map((s) => (
              <div
                key={s.label}
                style={{
                  background: "#fff",
                  border: "1px solid #ebebeb",
                  borderRadius: 10,
                  padding: "14px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#1a1a2e",
                      lineHeight: 1,
                      marginBottom: 4,
                    }}
                  >
                    {s.value}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa" }}>{s.sub}</div>
                </div>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: s.color + "15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={s.icon}
                    style={{ color: s.color, fontSize: 18 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── tab: datos del cliente ────────────────────────────────────────────────

  renderTabDatos() {
    const { profile, stats, purchases, clustersData } = this.state;
    const { id } = this.props.match.params;
    const profileData = profile.Profile || {};
    const recentPurchases = purchases.slice(0, 5);
    const clusters = clustersData ? clustersData.clusters : [];

    return (
      <div className='row' style={{ margin: 0 }}>
        {/* izquierda: info personal + info comercial */}
        <div className='col-12 col-md-4 mb-3 px-0 px-md-3'>
          <div className='card mb-3'>
            <div className='card-body' style={{ padding: "12px 16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>
                  Información Personal
                </div>
                <Link to={`${process.env.PUBLIC_URL}/edit-user/${id}`}>
                  <i
                    className='fa fa-pencil'
                    style={{ color: "#ccc", fontSize: 12 }}
                  />
                </Link>
              </div>
              <InfoRow
                label='Cliente'
                value={[profile.name, profile.last_name]
                  .filter(Boolean)
                  .join(" ")}
              />
              <InfoRow
                label={profile.type_document || "Documento"}
                value={profile.nro_document}
              />
              <InfoRow label='Teléfono' value={profileData.cell_phonenumber} />
              <InfoRow label='Email' value={profile.email} />
              <InfoRow
                label='Nacimiento'
                value={
                  profileData.birthdate
                    ? fmtDate(profileData.birthdate)
                    : profile.date_birthday
                      ? fmtDate(profile.date_birthday)
                      : null
                }
              />
              <InfoRow
                label='Género'
                value={GENDER_MAP[profileData.gender] || profileData.gender}
              />
              <InfoRow label='Alias' value={profile.alias} />
              <InfoRow label='Rol' value={profile.role} />
            </div>
          </div>

        </div>

        {/* centro: resumen + últimas compras */}
        <div className='col-12 col-md-5 mb-3 px-0 px-md-3'>
          <div className='card mb-3'>
            <div className='card-body' style={{ padding: "12px 16px" }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 10,
                }}
              >
                Resumen de Compras
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px 0" }}>
                {[
                  { label: "Órdenes", value: stats ? stats.total_orders : "—" },
                  {
                    label: "Monto total",
                    value: stats ? fmtMoney(stats.total_amount) : "—",
                  },
                  {
                    label: "Frecuencia",
                    value: stats ? stats.frequency + "/mes" : "—",
                  },
                  {
                    label: "1ª compra",
                    value: stats ? fmtDate(stats.first_purchase) : "—",
                  },
                  {
                    label: "Últ. compra",
                    value:
                      profile.days_latest_purchase_date != null
                        ? profile.days_latest_purchase_date + " días"
                        : "—",
                  },
                ].map((item, idx) => (
                  <div
                    key={item.label}
                    style={{ paddingRight: 8 }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#bbb",
                        marginBottom: 3,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#2d3436",
                      }}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='card-body' style={{ padding: "12px 16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>
                  Últimas Compras
                </div>
                <span
                  style={{ fontSize: 11, color: "#007bff", cursor: "pointer" }}
                  onClick={() => this.setState({ activeTab: 1 })}
                >
                  Ver todas →
                </span>
              </div>
              {recentPurchases.length === 0 ? (
                <p style={{ color: "#ccc", fontSize: 12, margin: 0 }}>
                  Sin compras registradas.
                </p>
              ) : (
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #ebebeb" }}>
                      {["Fecha", "N° Pedido", "Monto", "Estado"].map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              padding: "4px 8px",
                              color: "#bbb",
                              fontWeight: 600,
                              textAlign: "left",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {recentPurchases.map((p) => (
                      <tr
                        key={p.id}
                        style={{ borderBottom: "1px solid #f8f8f8" }}
                      >
                        <td
                          style={{
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            color: "#666",
                          }}
                        >
                          {fmtDate(p.date_programmed || p.date)}
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            fontSize: 11,
                            color: "#888",
                          }}
                        >
                          {p.id}
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          {fmtMoney(p.total)}
                        </td>
                        <td style={{ padding: "6px 8px" }}>
                          <PurchaseBadge status={p.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* derecha: dirección + segmentos */}
        <div className='col-12 col-md-3 mb-3 px-0 px-md-3'>
          <div className='card mb-3'>
            <div className='card-body' style={{ padding: "12px 16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>
                  Dirección principal
                </div>
                <Link
                  to={`${process.env.PUBLIC_URL}/address-user/${id}`}
                  style={{ fontSize: 11, color: "#007bff" }}
                >
                  Ver todas →
                </Link>
              </div>
              {profile.primary_address ? (
                <div style={{ display: "flex", gap: 6 }}>
                  <i
                    className='fa fa-map-marker'
                    style={{
                      color: "#e74c3c",
                      marginTop: 2,
                      flexShrink: 0,
                      fontSize: 12,
                    }}
                  />
                  <div style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: "#333" }}>
                      {profile.primary_address.address}{" "}
                      {profile.primary_address.number}
                    </div>
                    {profile.primary_address.interior && (
                      <div style={{ color: "#888" }}>
                        Int: {profile.primary_address.interior}
                      </div>
                    )}
                    <div style={{ color: "#888" }}>
                      {profile.primary_address.district}
                    </div>
                    {profile.primary_address.leave_your_order && (
                      <div style={{ color: "#aaa", marginTop: 2 }}>
                        Ref: {profile.primary_address.leave_your_order}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>
                  Sin dirección.
                </p>
              )}
            </div>
          </div>

          <div className='card'>
            <div className='card-body' style={{ padding: "12px 16px" }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 8,
                }}
              >
                Listas / Grupos
              </div>
              {clusters.length === 0 ? (
                <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>
                  Sin grupos asignados.
                </p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {clusters.map(
                    (m) =>
                      m.Cluster && (
                        <span
                          key={m.id_cluster}
                          style={{
                            background: "#e8f5e9",
                            color: "#2e7d32",
                            border: "1px solid #a5d6a7",
                            borderRadius: 20,
                            padding: "2px 10px",
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {m.Cluster.cluster_name}
                        </span>
                      ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── pagination helper ────────────────────────────────────────────────────

  renderPagination(total, page, pageSize, onPageChange) {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderTop: '1px solid #f0f0f0', gap: 8,
        background: '#fafafa', fontSize: 13, color: '#666',
      }}>
        <span style={{ fontSize: 12, color: '#aaa' }}>
          {from}–{to} de {total}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            style={{
              border: '1px solid #ddd', borderRadius: 6, padding: '4px 12px',
              background: page === 1 ? '#f4f4f4' : '#fff',
              color: page === 1 ? '#ccc' : '#333',
              cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
            }}
          >
            <i className='fa fa-chevron-left' />
          </button>
          <span style={{ padding: '4px 10px', fontSize: 13, color: '#555' }}>
            {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
            style={{
              border: '1px solid #ddd', borderRadius: 6, padding: '4px 12px',
              background: page === totalPages ? '#f4f4f4' : '#fff',
              color: page === totalPages ? '#ccc' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13,
            }}
          >
            <i className='fa fa-chevron-right' />
          </button>
        </div>
      </div>
    );
  }

  // ── tab: compras en detalle ───────────────────────────────────────────────

  renderTabPurchases() {
    const { purchases, purchasesPage } = this.state;
    if (purchases.length === 0) {
      return (
        <p style={{ color: "#ccc", padding: 20, margin: 0 }}>
          Sin compras registradas.
        </p>
      );
    }
    const PAGE_SIZE = 20;
    const paginated = purchases.slice((purchasesPage - 1) * PAGE_SIZE, purchasesPage * PAGE_SIZE);
    return (
      <div className='card'>
        <div className='card-body' style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f8f9fa",
                    borderBottom: "2px solid #ebebeb",
                  }}
                >
                  {[
                    { label: "#", title: "Token / ID" },
                    { label: "F. Pedido", title: "Fecha en que se realizó el pedido" },
                    { label: "F. Entrega", title: "Fecha de entrega programada" },
                    { label: "Distrito", title: "" },
                    { label: "Subtotal", title: "" },
                    { label: "Dto.", title: "Descuento" },
                    { label: "Delivery", title: "" },
                    { label: "Cred.", title: "Créditos usados" },
                    { label: "Total", title: "" },
                    { label: "Cobro", title: "Estado de cobro" },
                    { label: "Entrega", title: "Estado de entrega" },
                    { label: "Estado", title: "" },
                  ].map((h) => (
                    <th
                      key={h.label}
                      title={h.title}
                      style={{
                        padding: "10px 12px",
                        color: "#888",
                        fontWeight: 600,
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => this.openPurchaseModal(p.id)}
                    style={{
                      borderBottom: "1px solid #f4f4f4",
                      cursor: "pointer",
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f0faf4")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                  >
                    <td
                      style={{
                        padding: "9px 12px",
                        whiteSpace: "nowrap",
                        color: "#27ae60",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      #{p.id}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {fmtDate(p.date)}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666" }}>
                      {p.date_programmed ? fmtDate(p.date_programmed) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px" }}>{p.district || "—"}</td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {fmtMoney((parseFloat(p.subtotal) || 0) + (parseFloat(p.discount) || 0))}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {p.discount > 0 ? (
                        <span style={{ color: "#27ae60", fontWeight: 500 }}>
                          -{fmtMoney(p.discount)}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {fmtMoney(p.delivery)}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {p.cash > 0 ? (
                        <span style={{ color: "#8e44ad", fontWeight: 500 }}>
                          {fmtMoney(p.cash)}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", fontWeight: 700 }}>
                      {fmtMoney((parseFloat(p.total) || 0) + (parseFloat(p.delivery) || 0))}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {(() => {
                        const sc = STATUS_CHARGE_MAP[p.status_charge];
                        return sc ? (
                          <span style={{
                            background: sc.bg, color: sc.color,
                            borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                          }}>{sc.label}</span>
                        ) : p.status === "COMPLETADO" ? (
                          <span style={{ background: "#d4edda", color: "#155724", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>Pagado</span>
                        ) : <span style={{ color: "#ccc" }}>—</span>;
                      })()}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <DeliveryStatus status={p.status_delivery} />
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <PurchaseBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {this.renderPagination(purchases.length, purchasesPage, PAGE_SIZE, (p) => this.setState({ purchasesPage: p }))}
        </div>
      </div>
    );
  }

  // ── tab: productos (qué compró, agrupado por SKU -- no por pedido) ────────

  renderTabProducts() {
    const { products, productsPage, productsSort, productsLoading, productsLoaded } = this.state;

    if (productsLoading) {
      return <p style={{ color: "#ccc", padding: 20, margin: 0 }}>Cargando...</p>;
    }
    if (!productsLoaded) {
      return null;
    }
    if (products.length === 0) {
      return (
        <p style={{ color: "#ccc", padding: 20, margin: 0 }}>
          Este cliente no tiene productos comprados.
        </p>
      );
    }

    const PAGE_SIZE = 20;
    const sorted = sortRows(products, productsSort);
    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const currentPage = Math.min(productsPage, totalPages);
    const paginated = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const headers = [
      { label: "Producto", key: "name", align: "left" },
      { label: "Categoría", key: "category", align: "left" },
      { label: "Subcategoría", key: "subcategory", align: "left" },
      { label: "Precio", key: "price", align: "right" },
      { label: "Unidades", key: "quantity", align: "right" },
      { label: "Total", key: "total", align: "right" },
    ];

    return (
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ebebeb" }}>
                  {headers.map((h) => (
                    <SortableTh
                      key={h.key}
                      header={h}
                      sort={productsSort}
                      onSort={(key) => this.handleProductsSort(key)}
                      style={{ textAlign: h.align }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((item) => (
                  <tr key={item.id_product} style={{ borderBottom: "1px solid #f4f4f4" }}>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", fontWeight: 500, textAlign: "left" }}>
                      {item.name}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666", textAlign: "left" }}>
                      {item.category}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666", textAlign: "left" }}>
                      {item.subcategory}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666", textAlign: "right" }}>
                      {fmtMoney(item.price)}
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                      <span style={{ background: "#d4edda", color: "#155724", borderRadius: 10, padding: "2px 9px", fontSize: 12, fontWeight: 600 }}>
                        {item.quantity}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap", fontWeight: 700, textAlign: "right" }}>
                      {fmtMoney(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {this.renderPagination(sorted.length, currentPage, PAGE_SIZE, (p) => this.setState({ productsPage: p }))}
        </div>
      </div>
    );
  }

  // ── tab: pagos ───────────────────────────────────────────────────────────

  renderTabPayments() {
    const { payments, paymentsPage, showCancelledPayments } = this.state;
    if (payments.length === 0) {
      return <p style={{ color: "#ccc", padding: 20, margin: 0 }}>Sin pagos registrados.</p>;
    }

    // Los anulados se ocultan por defecto; solo se muestran para auditoría.
    const cancelledCount = payments.filter(isChargeCancelled).length;
    const visible = showCancelledPayments ? payments : payments.filter((c) => !isChargeCancelled(c));

    const mobile = isMobile();
    const PAGE_SIZE = 20;
    const paginated = visible.slice((paymentsPage - 1) * PAGE_SIZE, paymentsPage * PAGE_SIZE);

    const cancelledToggle = cancelledCount > 0 ? (
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: mobile ? '0 12px 10px' : '0 0 10px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#666', cursor: 'pointer', minHeight: 36, margin: 0 }}>
          <input
            type="checkbox"
            checked={showCancelledPayments}
            onChange={(e) => this.setState({ showCancelledPayments: e.target.checked, paymentsPage: 1 })}
          />
          Mostrar anulados ({cancelledCount})
        </label>
      </div>
    ) : null;

    if (visible.length === 0) {
      return (
        <div>
          {cancelledToggle}
          <p style={{ color: "#ccc", padding: 20, margin: 0 }}>Sin pagos registrados.</p>
        </div>
      );
    }

    if (mobile) {
      return (
        <div>
          {cancelledToggle}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 12px 12px' }}>
            {paginated.map((c) => {
              const cancelled = isChargeCancelled(c);
              const cs  = CHARGE_STATE_MAP[chargeStateKey(c)]  || { label: c.state  || '—', bg: '#f4f4f4', color: '#888' };
              const src = CHARGE_SOURCE_MAP[c.source] || { label: c.source || '—', icon: 'fa-credit-card', color: '#888' };
              const sc  = c.Purchase ? STATUS_CHARGE_MAP[c.Purchase.status_charge] : null;
              return (
                <div
                  key={c.id}
                  onClick={() => this.openChargeModal(c)}
                  style={{
                    background: '#fff', border: '1px solid #ebebeb', borderRadius: 10,
                    padding: '14px', cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    opacity: cancelled ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        background: src.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={'fa ' + src.icon} style={{ color: src.color, fontSize: 16 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#888' }}>{src.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#222', textDecoration: cancelled ? 'line-through' : 'none' }}>
                          S/ {parseFloat(c.value || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <i className='fa fa-chevron-right' style={{ color: '#ccc', fontSize: 13, marginTop: 4 }} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    <span style={{ background: cs.bg, color: cs.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{cs.label}</span>
                    {sc && <span style={{ background: sc.bg, color: sc.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{sc.label}</span>}
                  </div>
                  {cancelled && c.cancel_reason && (
                    <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic', marginBottom: 8 }}>{c.cancel_reason}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#aaa' }}>
                    <span>{c.Purchase ? `Pedido #${c.Purchase.id}` : '—'}</span>
                    <span style={{ textAlign: 'right' }}>
                      {chargeDateTime(c).main || (c.Purchase ? fmtDate(c.Purchase.date) : '—')}
                      {chargeDateTime(c).paymentDay && <div style={{ fontSize: 11 }}>pago: {chargeDateTime(c).paymentDay}</div>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {this.renderPagination(visible.length, paymentsPage, PAGE_SIZE, (p) => this.setState({ paymentsPage: p }))}
        </div>
      );
    }

    return (
      <div>
      {cancelledToggle}
      <div className='card'>
        <div className='card-body' style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ebebeb" }}>
                  {[
                    { label: "Pedido" }, { label: "F. Pedido" }, { label: "Método" },
                    { label: "Monto" }, { label: "F. Pago" }, { label: "Referencia" },
                    { label: "Estado pago" }, { label: "Cobro pedido" }, { label: "" },
                  ].map((h) => (
                    <th key={h.label} style={{ padding: "10px 12px", color: "#888", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((c) => {
                  const cancelled = isChargeCancelled(c);
                  const cs  = CHARGE_STATE_MAP[chargeStateKey(c)]  || { label: c.state  || "—", bg: "#f4f4f4", color: "#888" };
                  const src = CHARGE_SOURCE_MAP[c.source] || { label: c.source || "—", icon: "fa-credit-card", color: "#888" };
                  const sc  = c.Purchase ? STATUS_CHARGE_MAP[c.Purchase.status_charge] : null;
                  return (
                    <tr key={c.id} onClick={() => this.openChargeModal(c)}
                      style={{ borderBottom: "1px solid #f4f4f4", cursor: "pointer", opacity: cancelled ? 0.6 : 1 }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0faf4")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                        {c.Purchase ? <span style={{ color: "#27ae60", fontWeight: 600 }}>#{c.Purchase.id}</span> : "—"}
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666" }}>
                        {c.Purchase ? fmtDate(c.Purchase.date) : "—"}
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <i className={"fa " + src.icon} style={{ color: src.color }} />
                          {src.label}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap", fontWeight: 700, color: "#222", textDecoration: cancelled ? "line-through" : "none" }}>
                        S/ {parseFloat(c.value || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap", color: "#666" }}>
                        {chargeDateTime(c).main || "—"}
                        {chargeDateTime(c).paymentDay && (
                          <div style={{ fontSize: 11, color: "#aaa" }}>pago: {chargeDateTime(c).paymentDay}</div>
                        )}
                      </td>
                      <td style={{ padding: "9px 12px", color: "#888", fontSize: 12 }}>
                        {c.reference || "—"}
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                        <span
                          title={cancelled && c.cancel_reason ? c.cancel_reason : undefined}
                          style={{ background: cs.bg, color: cs.color, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}
                        >
                          {cs.label}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                        {sc ? <span style={{ background: sc.bg, color: sc.color, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{sc.label}</span> : "—"}
                      </td>
                      <td style={{ padding: "9px 12px" }}>
                        {c.voucher_image && (
                          <button
                            onClick={(e) => { e.stopPropagation(); this.openVoucher(c.voucher_image); }}
                            style={{ background: '#f0faf4', border: '1px solid #c3e6cb', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: 12, color: '#27ae60', fontWeight: 600 }}
                          >
                            <i className="fa fa-image" /> Ver
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {this.renderPagination(visible.length, paymentsPage, PAGE_SIZE, (p) => this.setState({ paymentsPage: p }))}
        </div>
      </div>
      </div>
    );
  }

  // ── tab: direcciones ─────────────────────────────────────────────────────

  renderTabAddresses() {
    const { addresses } = this.state;
    const { id } = this.props.match.params;
    if (addresses.length === 0) {
      return (
        <p style={{ color: "#ccc", padding: 20, margin: 0 }}>
          Sin direcciones registradas.
        </p>
      );
    }
    return (
      <div className='row' style={{ margin: 0 }}>
        {addresses.map((a, idx) => (
          <div key={a.id} className='col-12 col-md-6 col-xl-4 mb-3 px-0 px-md-3'>
            <div className='card h-100'>
              <div className='card-body' style={{ padding: "16px 18px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "#aaa",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    {idx === 0 ? "★ Principal" : "Dirección " + (idx + 1)}
                  </span>
                  <Link
                    to={`${process.env.PUBLIC_URL}/user/${id}/address/${a.id}`}
                    style={{ fontSize: 12, color: "#007bff" }}
                  >
                    <i className='fa fa-pencil' />
                  </Link>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <i
                    className='fa fa-map-marker'
                    style={{ color: "#e74c3c", marginTop: 2, flexShrink: 0 }}
                  />
                  <div style={{ fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>
                      {a.address} {a.number}
                    </div>
                    {a.interior && (
                      <div style={{ color: "#888" }}>Int: {a.interior}</div>
                    )}
                    <div style={{ color: "#888" }}>{a.district}</div>
                    {a.leave_your_order && (
                      <div style={{ color: "#aaa", marginTop: 3 }}>
                        Ref: {a.leave_your_order}
                      </div>
                    )}
                    {a.delivery != null && (
                      <div
                        style={{
                          color: "#27ae60",
                          marginTop: 4,
                          fontWeight: 500,
                        }}
                      >
                        Delivery: {fmtMoney(a.delivery)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── tab: campañas ────────────────────────────────────────────────────────

  renderTabCampaigns() {
    const { clustersData } = this.state;
    if (!clustersData)
      return (
        <p style={{ color: "#ccc", padding: 20, margin: 0 }}>Sin datos.</p>
      );

    const { whatsapp_campaigns, profile_campaign_log } = clustersData;
    const hasWa = whatsapp_campaigns && whatsapp_campaigns.length > 0;
    const hasLog = profile_campaign_log && profile_campaign_log.length > 0;

    return (
      <div className='row' style={{ margin: 0 }}>
        {/* campañas de WhatsApp (via cluster) */}
        <div className='col-12 col-md-7 mb-3 px-0 px-md-3'>
          <div className='card'>
            <div className='card-body' style={{ padding: "16px 20px" }}>
              <h6 style={{ margin: "0 0 12px", fontWeight: 600 }}>
                Campañas WhatsApp (vía grupos)
              </h6>
              {!hasWa ? (
                <p style={{ color: "#ccc", fontSize: 13, margin: 0 }}>
                  Sin campañas para sus grupos.
                </p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#f8f9fa",
                          borderBottom: "2px solid #ebebeb",
                        }}
                      >
                        {["Campaña", "Estado", "Programada", "Enviada"].map(
                          (h) => (
                            <th
                              key={h}
                              style={{
                                padding: "8px 12px",
                                color: "#888",
                                fontWeight: 600,
                                textAlign: "left",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {whatsapp_campaigns.map((c) => (
                        <tr
                          key={c.id}
                          style={{ borderBottom: "1px solid #f4f4f4" }}
                        >
                          <td style={{ padding: "8px 12px", fontWeight: 500 }}>
                            {c.campaign_name}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            <span
                              style={{
                                background:
                                  c.campaign_status === "SENT"
                                    ? "#d4edda"
                                    : "#fff3cd",
                                color:
                                  c.campaign_status === "SENT"
                                    ? "#155724"
                                    : "#856404",
                                borderRadius: 10,
                                padding: "2px 9px",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {c.campaign_status}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "8px 12px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtDate(c.scheduled_date)}
                          </td>
                          <td
                            style={{
                              padding: "8px 12px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtDate(c.sent_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* log de campaña de perfil */}
        <div className='col-12 col-md-5 mb-3 px-0 px-md-3'>
          <div className='card'>
            <div className='card-body' style={{ padding: "16px 20px" }}>
              <h6 style={{ margin: "0 0 12px", fontWeight: 600 }}>
                Campaña de actualización de perfil
              </h6>
              {!hasLog ? (
                <p style={{ color: "#ccc", fontSize: 13, margin: 0 }}>
                  Sin registros.
                </p>
              ) : (
                profile_campaign_log.map((log) => (
                  <div
                    key={log.week_identifier}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "7px 0",
                      borderBottom: "1px solid #f4f4f4",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: "#555" }}>{log.week_identifier}</span>
                    <span
                      style={{
                        background: log.completed_at ? "#d4edda" : "#fff3cd",
                        color: log.completed_at ? "#155724" : "#856404",
                        borderRadius: 10,
                        padding: "2px 9px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {log.completed_at ? "Completó" : "Vio"}
                    </span>
                    <span style={{ color: "#aaa" }}>
                      {fmtDate(log.completed_at || log.shown_at)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── tab: listas / segmentos ───────────────────────────────────────────────

  renderTabSegments() {
    const { clustersData } = this.state;
    if (!clustersData || !clustersData.clusters.length) {
      return (
        <p style={{ color: "#ccc", padding: 20, margin: 0 }}>
          Sin grupos asignados.
        </p>
      );
    }
    return (
      <div className='row' style={{ margin: 0 }}>
        {clustersData.clusters.map((m) => {
          if (!m.Cluster) return null;
          const c = m.Cluster;
          return (
            <div key={m.id_cluster} className='col-12 col-sm-6 col-lg-4 mb-3 px-0 px-md-3'>
              <div className='card h-100'>
                <div className='card-body' style={{ padding: "16px 20px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          marginBottom: 4,
                        }}
                      >
                        {c.cluster_name}
                      </div>
                      {c.cluster_code && (
                        <div style={{ fontSize: 12, color: "#aaa" }}>
                          Código: {c.cluster_code}
                        </div>
                      )}
                    </div>
                    <span
                      style={{
                        background:
                          c.cluster_status === "ACTIVE" ? "#e8f5e9" : "#f5f5f5",
                        color:
                          c.cluster_status === "ACTIVE" ? "#2e7d32" : "#888",
                        borderRadius: 10,
                        padding: "2px 9px",
                        fontSize: 12,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {c.cluster_status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#bbb", marginTop: 10 }}>
                    Miembro desde {fmtDate(m.created_at)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── charge detail modal ───────────────────────────────────────────────────

  renderChargeModal() {
    const { chargeModal } = this.state;
    if (!chargeModal) return null;
    const c = chargeModal;
    const cancelled = isChargeCancelled(c);
    const cs = CHARGE_STATE_MAP[chargeStateKey(c)] || { label: c.state || '—', bg: '#f4f4f4', color: '#888' };
    const cancelledBy = c.CancelledBy ? `${c.CancelledBy.name || ''} ${c.CancelledBy.last_name || ''}`.trim() : '';
    const cancelledAt = c.cancelled_at || c.deleted_at;
    const src = CHARGE_SOURCE_MAP[c.source] || { label: c.source || '—', icon: 'fa-credit-card', color: '#888' };
    const sc = c.Purchase ? STATUS_CHARGE_MAP[c.Purchase.status_charge] : null;
    const mobile = isMobile();
    return (
      <div
        onClick={() => this.setState({ chargeModal: null })}
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: mobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: mobile ? 0 : 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: mobile ? '16px 16px 0 0' : 12,
            width: '100%',
            maxWidth: mobile ? '100%' : 420,
            boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          }}
        >
          {/* header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 20px', borderBottom: '1px solid #ebebeb', background: '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className='fa fa-credit-card' style={{ color: '#27ae60', fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>Detalle del pago</span>
              <span style={{ background: cs.bg, color: cs.color, borderRadius: 10, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>
                {cs.label}
              </span>
            </div>
            <button
              onClick={() => this.setState({ chargeModal: null })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 18, padding: 4 }}
            >
              <i className='fa fa-times' />
            </button>
          </div>

          {/* body */}
          <div style={{ padding: mobile ? '16px' : '20px' }}>
            {/* método + monto */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px', borderRadius: 10,
              background: src.color + '0d', border: '1px solid ' + src.color + '30',
              marginBottom: 16,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: src.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={'fa ' + src.icon} style={{ color: src.color, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.3px' }}>
                  {src.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#222' }}>
                  S/ {parseFloat(c.value || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* filas de info */}
            {[
              { label: 'Fecha de pago',  value: chargeDateTime(c).main || '—' },
              { label: 'Fecha ingresada', value: chargeDateTime(c).paymentDay },
              { label: 'Referencia',     value: c.reference || '—' },
              { label: 'Pedido',         value: c.Purchase ? `#${c.Purchase.id} · ${fmtDate(c.Purchase.date)}` : '—' },
              { label: 'Total pedido',   value: c.Purchase ? `S/ ${parseFloat(c.Purchase.total || 0).toFixed(2)}` : null },
              { label: 'Cobro pedido',   value: sc ? sc.label : null, color: sc ? sc.color : null },
              { label: 'Anulado por',    value: cancelled ? (cancelledBy || 'Sin registro') : null },
              { label: 'Fecha anulación', value: cancelled && cancelledAt ? Moment(cancelledAt).format('YYYY-MM-DD HH:mm') : null },
              { label: 'Motivo',         value: cancelled ? (c.cancel_reason || 'Sin registro') : null },
            ].map(({ label, value, color }) => value != null ? (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid #f4f4f4', fontSize: 13,
              }}>
                <span style={{ color: '#aaa' }}>{label}</span>
                <span style={{ color: color || '#333', fontWeight: 500 }}>{value}</span>
              </div>
            ) : null)}

            {/* voucher */}
            <div style={{ marginTop: 16 }}>
              {c.voucher_image ? (
                <button
                  onClick={() => this.openVoucher(c.voucher_image)}
                  style={{
                    width: '100%', padding: '10px',
                    background: '#f0faf4', border: '1px solid #c3e6cb',
                    borderRadius: 8, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    fontSize: 13, color: '#27ae60', fontWeight: 600,
                  }}
                >
                  <i className='fa fa-image' />
                  Ver voucher de pago
                </button>
              ) : (
                <div style={{
                  width: '100%', padding: '10px',
                  background: '#f8f9fa', border: '1px dashed #ddd',
                  borderRadius: 8, textAlign: 'center',
                  fontSize: 13, color: '#bbb',
                }}>
                  <i className='fa fa-image' style={{ marginRight: 6 }} />
                  Sin voucher adjunto
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── voucher viewer ────────────────────────────────────────────────────────

  renderVoucherModal() {
    const { voucherImage } = this.state;
    if (!voucherImage) return null;
    const user = AuthService.getCurrentUser();
    const src = `${API_URL}voucher/${voucherImage}?token=${user ? user.token : ''}`;
    return (
      <div
        onClick={() => this.setState({ voucherImage: null })}
        style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: 520, width: '100%' }}>
          <button
            onClick={() => this.setState({ voucherImage: null })}
            style={{
              position: 'absolute', top: -14, right: -14, zIndex: 1,
              background: '#fff', border: 'none', borderRadius: '50%',
              width: 32, height: 32, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, color: '#555',
            }}
          >
            <i className='fa fa-times' />
          </button>
          <img
            src={src}
            alt='Voucher'
            data-protected='1'
            style={{
              width: '100%', maxHeight: '80vh', objectFit: 'contain',
              borderRadius: 10, display: 'block',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          />
        </div>
      </div>
    );
  }

  // ── purchase detail modal ─────────────────────────────────────────────────

  renderPurchaseModal() {
    const { purchaseModal, purchaseModalLoading } = this.state;
    if (!purchaseModal && !purchaseModalLoading) return null;

    const p = purchaseModal !== 'loading' ? purchaseModal : null;
    const items = p && p.Purchases_detail ? p.Purchases_detail : [];

    const PURCHASE_DELIVERY_STATUS_MAP = {
      'POR PREPARAR': { label: 'Por preparar', bg: '#fdecea', color: '#c0392b' },
      'LISTO PARA ENVIO': { label: 'Listo para envío', bg: '#fff8e1', color: '#e67e22' },
      ENTREGADO: { label: 'Entregado', bg: '#eafaf1', color: '#27ae60' },
    };
    const ds = p && p.status_delivery ? (PURCHASE_DELIVERY_STATUS_MAP[p.status_delivery] || { label: p.status_delivery, bg: '#f4f4f4', color: '#888' }) : null;
    const ps = p ? (PURCHASE_STATUS_MAP[p.status] || { label: p.status || '—', bg: '#e9ecef', color: '#495057' }) : null;
    const sc = p && p.status_charge ? (STATUS_CHARGE_MAP[p.status_charge] || { label: p.status_charge, bg: '#f4f4f4', color: '#888' }) : null;

    const mobile = isMobile();
    return (
      <div
        onClick={() => this.closePurchaseModal()}
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: mobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: mobile ? 0 : '16px',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: mobile ? '16px 16px 0 0' : 12,
            width: '100%',
            maxWidth: mobile ? '100%' : 680,
            maxHeight: mobile ? '95vh' : '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          }}
        >
          {/* header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px', borderBottom: '1px solid #ebebeb',
            background: '#fafafa', flexShrink: 0, flexWrap: 'wrap', gap: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className='fa fa-shopping-bag' style={{ color: '#27ae60', fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>
                {p ? `Pedido #${p.id}` : 'Detalle de pedido'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {p && ps && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#bbb', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Estado</div>
                  <span style={{ background: ps.bg, color: ps.color, borderRadius: 10, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{ps.label}</span>
                </div>
              )}
              {p && ds && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#bbb', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Entrega</div>
                  <span style={{ background: ds.bg, color: ds.color, borderRadius: 10, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{ds.label}</span>
                </div>
              )}
              {p && sc && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#bbb', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Cobro</div>
                  <span style={{ background: sc.bg, color: sc.color, borderRadius: 10, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{sc.label}</span>
                </div>
              )}
              <button
                onClick={() => this.closePurchaseModal()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 18, padding: 4, lineHeight: 1 }}
              >
                <i className='fa fa-times' />
              </button>
            </div>
          </div>

          {/* body */}
          <div style={{ overflowY: 'auto', padding: mobile ? '0 12px 20px' : '0 20px 20px', flexGrow: 1 }}>
            {purchaseModalLoading && (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <i className='fa fa-spinner fa-spin fa-2x' style={{ color: '#ddd' }} />
              </div>
            )}

            {p && (
              <>
                {/* meta info */}
                <div style={{
                  display: 'flex', gap: 24, flexWrap: 'wrap',
                  padding: '14px 0 12px', borderBottom: '1px solid #f0f0f0',
                  fontSize: 12, color: '#888',
                }}>
                  <div>
                    <span style={{ marginRight: 5 }}>
                      <i className='fa fa-calendar' />
                    </span>
                    Pedido: <strong style={{ color: '#444' }}>{fmtDate(p.date)}</strong>
                  </div>
                  {p.date_programmed && (
                    <div>
                      <span style={{ marginRight: 5 }}>
                        <i className='fa fa-truck' />
                      </span>
                      Entrega:{' '}
                      <strong style={{ color: '#444' }}>
                        {fmtDate(p.date_programmed)}
                        {p.hour_programmed_start && (
                          <> · {p.hour_programmed_start.slice(0, 5)}–{p.hour_programmed_end ? p.hour_programmed_end.slice(0, 5) : '?'}</>
                        )}
                      </strong>
                    </div>
                  )}
                  {p.district && (
                    <div>
                      <span style={{ marginRight: 5 }}>
                        <i className='fa fa-map-marker' />
                      </span>
                      <strong style={{ color: '#444' }}>
                        {[p.address, p.number, p.interior].filter(Boolean).join(' ')}
                        {p.district && `, ${p.district}`}
                      </strong>
                    </div>
                  )}
                </div>

                {/* items table */}
                {items.length > 0 ? (
                  <div style={{ overflowX: 'auto', marginTop: 14 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ebebeb' }}>
                        <th style={{ padding: '6px 8px', color: '#aaa', fontWeight: 600, textAlign: 'left', width: 36 }} />
                        <th style={{ padding: '6px 8px', color: '#aaa', fontWeight: 600, textAlign: 'left' }}>Producto</th>
                        <th style={{ padding: '6px 8px', color: '#aaa', fontWeight: 600, textAlign: 'center' }}>Cant.</th>
                        <th style={{ padding: '6px 8px', color: '#aaa', fontWeight: 600, textAlign: 'right' }}>Precio</th>
                        <th style={{ padding: '6px 8px', color: '#aaa', fontWeight: 600, textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const prod = item.Product || {};
                        const hasDiscount = item.total_no_discount && parseFloat(item.total_no_discount) > parseFloat(item.total);
                        return (
                          <tr key={item.id} style={{ borderBottom: '1px solid #f4f4f4' }}>
                            <td style={{ padding: '8px 8px' }}>
                              {prod.image_1 ? (
                                <img
                                  src={prod.image_1}
                                  alt=''
                                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, border: '1px solid #ebebeb' }}
                                />
                              ) : (
                                <div style={{
                                  width: 32, height: 32, borderRadius: 6,
                                  background: '#f0faf4', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <i className='fa fa-leaf' style={{ color: '#27ae60', fontSize: 12 }} />
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px 8px' }}>
                              <span style={{ fontWeight: 500, color: '#333' }}>{prod.name || '—'}</span>
                              {hasDiscount && (
                                <span style={{
                                  marginLeft: 6, background: '#eafaf1', color: '#27ae60',
                                  fontSize: 11, borderRadius: 8, padding: '1px 6px', fontWeight: 600,
                                }}>
                                  desc.
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '8px 8px', textAlign: 'center', color: '#555' }}>
                              {item.quantity_requested && item.quantity_requested !== item.quantity ? (
                                <span>
                                  <span style={{ textDecoration: 'line-through', color: '#ccc', marginRight: 4 }}>
                                    {item.quantity_requested}
                                  </span>
                                  {item.quantity}
                                </span>
                              ) : (
                                item.quantity
                              )}
                            </td>
                            <td style={{ padding: '8px 8px', textAlign: 'right', color: '#666' }}>
                              S/ {parseFloat(item.price || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#222' }}>
                              {hasDiscount && (
                                <div style={{ fontSize: 11, color: '#bbb', textDecoration: 'line-through', fontWeight: 400 }}>
                                  S/ {parseFloat(item.total_no_discount).toFixed(2)}
                                </div>
                              )}
                              S/ {parseFloat(item.total || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <p style={{ color: '#ccc', padding: '20px 0', margin: 0 }}>Sin productos registrados.</p>
                )}

                {/* pagos (charges) */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.4px', marginBottom: 8 }}>
                    Pagos registrados
                  </div>
                  {(!p.Charge || p.Charge.length === 0) && (
                    <p style={{ color: '#ccc', fontSize: 13, margin: 0 }}>Sin pagos registrados.</p>
                  )}
                  {p.Charge && p.Charge.map((c) => {
                    const cs = CHARGE_STATE_MAP[c.state] || { label: c.state || '—', bg: '#f4f4f4', color: '#888' };
                    const src = CHARGE_SOURCE_MAP[c.source] || { label: c.source || '—', icon: 'fa-credit-card', color: '#888' };
                    return (
                      <div key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 8, marginBottom: 6,
                        background: '#f8f9fa', border: '1px solid #ebebeb',
                      }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          background: src.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <i className={'fa ' + src.icon} style={{ color: src.color, fontSize: 15 }} />
                        </div>
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>
                              S/ {parseFloat(c.value || 0).toFixed(2)}
                            </span>
                            <span style={{ fontSize: 12, color: '#666' }}>{src.label}</span>
                            <span style={{ background: cs.bg, color: cs.color, borderRadius: 8, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                              {cs.label}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                            {c.date_payment ? fmtDate(c.date_payment) : ''}
                            {c.reference ? ` · Ref: ${c.reference}` : ''}
                          </div>
                        </div>
                        {c.voucher_image && (
                          <button
                            onClick={() => this.openVoucher(c.voucher_image)}
                            style={{
                              flexShrink: 0, background: '#f0faf4', border: '1px solid #c3e6cb',
                              borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5,
                              fontSize: 12, color: '#27ae60', fontWeight: 600,
                            }}
                          >
                            <i className='fa fa-image' />
                            Ver voucher
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* totals */}
                <div style={{
                  marginTop: 16, borderTop: '2px solid #ebebeb',
                  paddingTop: 14, display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
                }}>
                  {/* badges informativos */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(parseFloat(p.discount) > 0 || (p.CodeAudit && p.CodeAudit.length > 0)) && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'flex-start', gap: 8,
                        background: '#eafaf1', border: '1px solid #c3e6cb',
                        borderRadius: 8, padding: '6px 12px',
                      }}>
                        <i className='fa fa-tag' style={{ color: '#27ae60', fontSize: 13, marginTop: 2 }} />
                        <div>
                          <div style={{ fontSize: 11, color: '#27ae60', fontWeight: 700, marginBottom: 2 }}>Descuento aplicado</div>
                          {p.CodeAudit && p.CodeAudit.map((ca) => (
                            <span key={ca.id} style={{
                              display: 'inline-block', marginRight: 4, marginBottom: 2,
                              background: '#27ae60', color: '#fff',
                              borderRadius: 6, padding: '1px 8px', fontSize: 12, fontWeight: 700,
                              letterSpacing: '0.5px',
                            }}>
                              {ca.code}
                            </span>
                          ))}
                          {parseFloat(p.discount) > 0 && (
                            <div style={{ fontSize: 14, color: '#155724', fontWeight: 700, marginTop: 2 }}>
                              -S/ {parseFloat(p.discount).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {parseFloat(p.cash) > 0 && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        background: '#f3e8ff', border: '1px solid #d8b4fe',
                        borderRadius: 8, padding: '6px 12px',
                      }}>
                        <i className='fa fa-star' style={{ color: '#8e44ad', fontSize: 13 }} />
                        <div>
                          <div style={{ fontSize: 11, color: '#8e44ad', fontWeight: 700 }}>Créditos usados</div>
                          <div style={{ fontSize: 14, color: '#6b21a8', fontWeight: 700 }}>S/ {parseFloat(p.cash).toFixed(2)}</div>
                        </div>
                      </div>
                    )}
                    {p.deferred_pay && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        background: '#fff8e1', border: '1px solid #ffc107',
                        borderRadius: 8, padding: '6px 12px',
                      }}>
                        <i className='fa fa-clock-o' style={{ color: '#e67e22', fontSize: 13 }} />
                        <div>
                          <div style={{ fontSize: 11, color: '#e67e22', fontWeight: 700 }}>Pago diferido</div>
                          <div style={{ fontSize: 12, color: '#7d5a00' }}>{p.deferred_pay}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* desglose numérico */}
                  <div style={{ minWidth: 220 }}>
                    {(() => {
                      // p.subtotal ya viene neto del descuento (p.discount se
                      // resta al armar el pedido) y p.total además le resta el
                      // cash -- "Productos" tiene que mostrar el bruto real
                      // (subtotal + discount, antes de cualquier descuento) y
                      // el descuento restarse acá explícitamente, para que
                      // este desglose cuadre con la fila de la tabla de
                      // "Subtotal" (que también muestra el bruto).
                      const discount = parseFloat(p.discount) || 0;
                      const productsGross = (parseFloat(p.subtotal) || 0) + discount;
                      const delivery = parseFloat(p.delivery) || 0;
                      const cash     = parseFloat(p.cash)     || 0;
                      const grandTotal = (parseFloat(p.total) || 0) + delivery;
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                            <span>Productos</span>
                            <span style={{ color: '#333', fontWeight: 500 }}>S/ {productsGross.toFixed(2)}</span>
                          </div>
                          {discount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                              <span>Descuento</span>
                              <span style={{ color: '#27ae60', fontWeight: 500 }}>-S/ {discount.toFixed(2)}</span>
                            </div>
                          )}
                          {delivery > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                              <span>Delivery</span>
                              <span style={{ color: '#333', fontWeight: 500 }}>S/ {delivery.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                            <span>Créditos usados</span>
                            <span style={{ color: cash > 0 ? '#8e44ad' : '#bbb', fontWeight: 500 }}>
                              {cash > 0 ? `-S/ ${cash.toFixed(2)}` : '—'}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between',
                            padding: '10px 0 0', borderTop: '1px solid #ebebeb',
                            marginTop: 6, fontSize: 15, fontWeight: 700, color: '#222',
                          }}>
                            <span>Total</span>
                            <span style={{ color: '#27ae60' }}>S/ {grandTotal.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── render principal ──────────────────────────────────────────────────────

  render() {
    const { loading, profile, activeTab } = this.state;

    return (
      <Fragment>
        <Breadcrumb title='Ficha de Cliente' parent='Clientes' parentLink={`${process.env.PUBLIC_URL}/list-user`} />
        <div className='container-fluid px-0 px-md-3'>
          {loading && (
            <div className='text-center' style={{ padding: 60 }}>
              <i
                className='fa fa-spinner fa-spin fa-2x'
                style={{ color: "#ddd" }}
              />
              <p style={{ color: "#bbb", marginTop: 12 }}>Cargando...</p>
            </div>
          )}

          {!loading && !profile && (
            <div className='alert alert-danger'>No se encontró el usuario.</div>
          )}

          {!loading && profile && (
            <Fragment>
              {this.renderHeader()}

              <div className='card'>
                <div className='card-body' style={{ padding: 0 }}>

                  {/* ── tab bar ── */}
                  <div style={{
                    display: 'flex',
                    overflowX: 'auto',
                    borderBottom: '2px solid #ebebeb',
                    background: '#fafafa',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                  }}>
                    {[
                      { icon: 'fa-user',         label: 'Datos' },
                      { icon: 'fa-shopping-cart', label: 'Compras' },
                      { icon: 'fa-credit-card',  label: 'Pagos' },
                      { icon: 'fa-map-marker',    label: 'Direcciones' },
                      { icon: 'fa-bullhorn',      label: 'Campañas' },
                      { icon: 'fa-tags',          label: 'Listas' },
                      { icon: 'fa-cubes',         label: 'Productos' },
                    ].map((t, i) => (
                      <button
                        key={t.label}
                        onClick={() => this.handleTabClick(i)}
                        style={{
                          flexShrink: 0,
                          padding: '11px 18px',
                          fontSize: 13,
                          fontWeight: activeTab === i ? 700 : 500,
                          color: activeTab === i ? '#27ae60' : '#888',
                          background: 'none',
                          border: 'none',
                          borderBottom: activeTab === i ? '2px solid #27ae60' : '2px solid transparent',
                          marginBottom: -2,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          outline: 'none',
                        }}
                      >
                        <i className={'fa ' + t.icon} style={{ marginRight: 6 }} />
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* ── tab content ── */}
                  {activeTab === 0 && <div style={{ padding: '16px 0 4px' }}>{this.renderTabDatos()}</div>}
                  {activeTab === 1 && <div style={{ padding: '16px 0' }}>{this.renderTabPurchases()}</div>}
                  {activeTab === 2 && <div style={{ padding: '16px 0' }}>{this.renderTabPayments()}</div>}
                  {activeTab === 3 && <div style={{ padding: '16px 0' }}>{this.renderTabAddresses()}</div>}
                  {activeTab === 4 && <div style={{ padding: '16px 0' }}>{this.renderTabCampaigns()}</div>}
                  {activeTab === 5 && <div style={{ padding: '16px 0' }}>{this.renderTabSegments()}</div>}
                  {activeTab === 6 && <div style={{ padding: '16px 0' }}>{this.renderTabProducts()}</div>}

                </div>
              </div>
            </Fragment>
          )}
        </div>
        {this.renderPurchaseModal()}
        {this.renderChargeModal()}
        {this.renderVoucherModal()}
      </Fragment>
    );
  }
}

export default VerCustomerProfile;
